import type { ReactNode } from 'react';
import type { EdiCatalogItem } from '@/api/hanly-v2/hanlyAPIV2.schemas';
import { ProductThumbnail } from '@/components/product-thumbnail';
import { useShowPrices } from '@/contexts/auth';
import { ProductAttachments } from './product-attachments';

const DetailRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <>
    <dt className="text-neutral-600">{label}</dt>
    <dd>{children}</dd>
  </>
);

const formatQuantityRange = (min: number, max: number | null, unit: string) =>
  max === null
    ? `${min.toLocaleString()}${unit}〜`
    : `${min.toLocaleString()}〜${max.toLocaleString()}${unit}`;

/**
 * 商品の詳細。表示設定で非表示にした項目はサーバが null で返すため、値がある項目だけ出す。
 */
export const ProductDetail = ({ item }: { item: EdiCatalogItem }) => {
  const showPrices = useShowPrices();
  const pv = item.product_variation;
  const unit = pv.unit_name ?? '';
  const snp = Number(pv.snp);
  const hasPackaging = pv.packaging_type_name && snp > 0 && snp !== 1;

  return (
    <div className="flex flex-col gap-4">
      <ProductThumbnail
        url={pv.thumbnail_url}
        size={320}
        className="aspect-square h-auto! w-full! max-w-80 self-center"
      />
      {showPrices && (
        <div className="text-xl font-bold">
          ¥{Number(pv.unit_price).toLocaleString()}
          {unit && <span className="text-sm font-normal text-neutral-600"> / {unit}</span>}
        </div>
      )}
      {showPrices && pv.quantity_prices && pv.quantity_prices.length > 0 && (
        <table className="w-full border border-neutral-200 text-sm">
          <caption className="bg-neutral-100 px-3 py-1 text-left font-bold">数量別単価</caption>
          <tbody>
            {pv.quantity_prices.map((qp) => (
              <tr key={qp.min_quantity} className="border-t border-neutral-200">
                <td className="px-3 py-1">{formatQuantityRange(qp.min_quantity, qp.max_quantity, unit)}</td>
                <td className="px-3 py-1 text-right">¥{Number(qp.price).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <ProductAttachments itemId={item.id} />
      <dl className="grid grid-cols-[6rem_1fr] gap-x-3 gap-y-1 text-sm">
        {pv.code && <DetailRow label="商品コード">{pv.code}</DetailRow>}
        {pv.model_number && <DetailRow label="品番">{pv.model_number}</DetailRow>}
        {pv.spec && <DetailRow label="規格">{pv.spec}</DetailRow>}
        {pv.manufacturer && <DetailRow label="メーカー">{pv.manufacturer}</DetailRow>}
        {hasPackaging && (
          <DetailRow label="入数">
            1{pv.packaging_type_name} = {snp.toLocaleString()}
            {unit}
          </DetailRow>
        )}
      </dl>
      {item.description && <p className="text-sm whitespace-pre-wrap">{item.description}</p>}
    </div>
  );
};
